import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Section, Eyebrow, FolioBadge, Timeline, StatusBadge } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money, pad, fmtDateTime, PAYMENT_STATUS } from "@/lib/format";
import { Search, ShieldQuestion, Ticket, Landmark, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const STATE_MAP = {
  "reservation.created": "Reserva creada",
  "payment.proof_uploaded": "Comprobante recibido",
  "payment.approved": "Pago confirmado",
  "payment.rejected": "Pago rechazado",
  "payment.correction_requested": "Corrección solicitada",
};

export default function Lookup() {
  const [sp] = useSearchParams();
  const [folio, setFolio] = useState(sp.get("folio") || "");
  const [contact, setContact] = useState(sp.get("contact") || "");
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (f = folio, c = contact) => {
    if (!f || !c) { toast.error("Ingresa folio y verificación"); return; }
    setLoading(true); setError("");
    try {
      const r = await api.post("/lookup", { folio: f.trim(), contact: c.trim() });
      setRes(r.data);
    } catch (e) {
      setRes(null);
      setError(e.response?.status === 403 ? "Verificación adicional requerida. Revisa el correo o WhatsApp registrado." : "Folio no encontrado.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (sp.get("folio") && sp.get("contact")) search(sp.get("folio"), sp.get("contact")); }, []); // eslint-disable-line

  const timeline = res ? [
    { label: "Reserva creada", done: true, at: fmtDateTime(res.created_at) },
    { label: "Pago pendiente", done: res.payment_status !== "pending", active: res.payment_status === "pending" },
    { label: "Comprobante recibido", done: ["in_review", "approved", "correction_requested"].includes(res.payment_status), active: false },
    { label: "Pago en revisión", done: res.payment_status === "approved", active: res.payment_status === "in_review" },
    { label: "Boletos confirmados", done: res.payment_status === "approved", active: false },
    { label: "Sorteo", done: false, active: false },
  ] : [];

  return (
    <Section className="py-14">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <Eyebrow><Search className="h-3.5 w-3.5" />Portal del cliente</Eyebrow>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-900">Consulta tu participación</h1>
          <p className="mt-2 text-slate-600">Sin crear cuenta. Solo necesitas tu folio y tu correo, WhatsApp o token seguro.</p>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label className="text-xs">Folio</Label><Input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="SR-2026-001528" className="mt-1.5 font-mono" data-testid="lookup-folio" /></div>
            <div><Label className="text-xs">Correo / WhatsApp / token</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="tu@correo.com" className="mt-1.5" data-testid="lookup-contact" /></div>
          </div>
          <Button onClick={() => search()} disabled={loading} className="mt-4 w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="lookup-submit">
            <Search className="mr-2 h-4 w-4" />Consultar
          </Button>
        </div>

        {error && (
          <div className="mt-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-800" data-testid="lookup-error">
            <ShieldQuestion className="h-5 w-5" /><span className="text-sm">{error}</span>
          </div>
        )}

        {res && (
          <div className="mt-8 space-y-6" data-testid="lookup-result">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3"><FolioBadge folio={res.folio} />
                  <StatusBadge {...(PAYMENT_STATUS[res.payment_status] || PAYMENT_STATUS.pending)} /></div>
                <span className="text-sm text-slate-500">{res.raffle_name}</span>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <Info label="Participante" value={`${res.customer?.name} ${res.customer?.lastname || ""}`} />
                <Info label="Total" value={money(res.total)} mono />
                <Info label="Boletos" value={res.numbers?.length} mono />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {res.numbers?.map((n) => <span key={n} className="rounded bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-brand">{pad(n)}</span>)}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="mb-5 font-semibold text-slate-900">Estado de tu operación</h3>
                <Timeline items={timeline} />
              </div>
              <div className="space-y-4">
                {res.payment_status === "correction_requested" && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 text-sm text-orange-800">
                    <b>Corrección solicitada.</b> Vuelve a subir tu comprobante desde el enlace en tu correo.
                  </div>
                )}
                {res.payment_status === "rejected" && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
                    <b>Pago rechazado.</b> Contáctanos por canales oficiales para ayudarte.
                  </div>
                )}
                {res.payment_status === "approved" && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
                    <b>¡Boletos confirmados!</b> Mucha suerte en el sorteo.
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><HelpCircle className="h-4 w-4 text-brand" />¿Necesitas ayuda?</h4>
                  <p className="mt-2 text-sm text-slate-500">Contáctanos únicamente por nuestros canales oficiales verificados.</p>
                  <Button asChild variant="outline" size="sm" className="mt-3"><Link to="/transparencia">Ver canales oficiales</Link></Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

const Info = ({ label, value, mono }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div className={`mt-0.5 font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div></div>
);
